import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, X, ArrowLeft, Trash2, Users, Calendar, Check, Link as LinkIcon } from 'lucide-react';
import {
  DndContext, DragOverlay, useDraggable, useDroppable,
  PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { api } from '../api/client';
import { SearchableSelect } from '../components/SearchableSelect';
import { ConfirmModal } from '../components/ConfirmModal';
import { BareTextarea } from '../components/ProjectForm';
import { useT } from '../i18n';
import { usePresence, useLiveChanges, setLiveFocus, focusMap, flashMoved, draggingOver, useTypingIn, type LivePerson, type LiveFocus } from '../live/live';
import { useLiveField, LiveDrafts, TypingBadge } from '../live/LiveField';
import { PresenceAvatars, FocusBadge, LiveGhost, LiveCursors } from '../live/Presence';
import { FilterSelect } from '../components/FilterSelect';
import { taskDisplayCode } from '../utils/identifiers';
import type {
  KanbanBoardItem, KanbanCard, KanbanMember, KanbanColumnKey, KanbanUser,
} from '../types';

// Standalone Kanban boards — shared whiteboards a user can create, invite
// teammates to, and drag cards across four fixed lanes. Separate from the
// per-project task board in TasksPage.

const COLUMN_DEFS: { key: KanbanColumnKey; labelKey: string }[] = [
  { key: 'backlog', labelKey: 'kanban.col.backlog' },
  { key: 'todo', labelKey: 'kanban.col.todo' },
  { key: 'in_progress', labelKey: 'kanban.col.inProgress' },
  { key: 'done', labelKey: 'kanban.col.done' },
  { key: 'archived', labelKey: 'kanban.col.archived' },
];

function isColumnKey(v: string): v is KanbanColumnKey {
  return COLUMN_DEFS.some(c => c.key === v);
}

// ── Draggable card ──

function DraggableCard({ card, onOpen, liveBy }: { card: KanbanCard; onOpen: () => void; liveBy?: LivePerson[] }) {
  const { t } = useT();
  const { user } = useAuth();
  // Who is typing in this card right now (✎).
  const typingBy = useTypingIn('card', user?.id).get(card.id);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card-${card.id}`,
    data: { card },
  });
  const isOverdue = !!card.due_date && new Date(card.due_date) < new Date() && card.column_key !== 'done' && card.column_key !== 'archived';
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="kb-card"
      data-dnd-draggable
      data-dnd-dragging={isDragging || undefined}
      style={{ opacity: isDragging ? 0.4 : 1, position: 'relative' }}
      onClick={onOpen}
      title={t('kanban.cardHint')}
    >
      <FocusBadge people={liveBy} />
      <TypingBadge people={typingBy} />
      <div className="kb-card-title">{card.title}</div>
      {card.description && <div className="kb-card-desc">{card.description}</div>}
      <div className="kb-card-meta">
        {card.due_date && (
          <span className={`kb-card-due${isOverdue ? ' kb-card-due--overdue' : ''}`}>
            <Calendar size={12} />
            {new Date(card.due_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
          </span>
        )}
        {card.assignee_name && <span className="kb-card-assignee">@{card.assignee_name}</span>}
        {/* Stable short id (issue #27) — lets people reference a card
            unambiguously in meetings. */}
        <span className="card-id">{taskDisplayCode(card)}</span>
      </div>
    </div>
  );
}

function CardOverlay({ card }: { card: KanbanCard }) {
  return (
    <div className="kb-card" data-dnd-overlay>
      <div className="kb-card-title">{card.title}</div>
      {card.description && <div className="kb-card-desc">{card.description}</div>}
    </div>
  );
}

// ── Column ──

function Column({
  columnKey,
  label,
  cards,
  onOpenCard,
  onAddCard,
  liveFocus,
  ghosts,
}: {
  columnKey: KanbanColumnKey;
  label: string;
  cards: KanbanCard[];
  onOpenCard: (id: number) => void;
  onAddCard: (title: string, dueDate: string | null) => void;
  liveFocus: Map<number, LivePerson[]>; // who moves or has open each card
  ghosts: { person: LivePerson; card: KanbanCard }[]; // cards someone else is dragging over this column
}) {
  const { t } = useT();
  const { setNodeRef, isOver } = useDroppable({ id: columnKey });
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');

  const submit = () => {
    if (!title.trim()) return;
    onAddCard(title.trim(), dueDate || null);
    setTitle('');
    setDueDate('');
    setAdding(false);
  };

  return (
    <div className="kb-column">
      <div className="kb-column-head">
        <span className="kb-column-label">{label}</span>
        <span className="kb-column-count">{cards.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className="kb-column-body"
        data-live-anchor={`kcol-${columnKey}`} // live pointers show only over the cards, not the column header
        data-dnd-over={isOver || undefined}
      >
        {ghosts.map(g => (
          <LiveGhost key={`ghost-${g.person.userId}`} person={g.person}><CardOverlay card={g.card} /></LiveGhost>
        ))}
        {cards.map(c => (
          <DraggableCard key={c.id} card={c} onOpen={() => onOpenCard(c.id)} liveBy={liveFocus.get(c.id)} />
        ))}
        {adding ? (
          <div className="kb-add-card open">
            <input
              className="form-input kb-add-card-input"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
                if (e.key === 'Escape') { setAdding(false); setTitle(''); setDueDate(''); }
              }}
              placeholder={t('kanban.cardTitlePlaceholder')}
            />
            <input
              className="form-input kb-add-card-input"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              title={t('kanban.dueDate')}
            />
            <div className="kb-add-card-actions">
              <button className="btn btn-accent btn-sm" onClick={submit} disabled={!title.trim()}>{t('common.add')}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setTitle(''); setDueDate(''); }}>
                <X size={14} />
              </button>
            </div>
          </div>
        ) : (
          <button className="kb-add-card" onClick={() => setAdding(true)}>
            <Plus size={14} /> {t('kanban.addCard')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Card edit modal ──

function CardModal({
  card,
  boardId,
  memberNames,
  onClose,
  onChange,
  onDeleted,
}: {
  card: KanbanCard;
  boardId: number;
  memberNames: { value: string; label: string }[];
  onClose: () => void;
  onChange: (patch: { title: string; description: string; assignee_name: string; due_date: string | null }) => Promise<void>;
  onDeleted: () => void;
}) {
  const { t } = useT();
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description || '');
  // Live title: the others with this card open see what is typed.
  const titleLive = useLiveField(`card:${card.id}:title`, title);
  const [assignee, setAssignee] = useState(card.assignee_name || '');
  const [dueDate, setDueDate] = useState(card.due_date || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Stable, shareable address for this card (issue: kanban cards had no fixed
  // URL — a board or notification link always landed on the list, never the
  // card itself).
  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}#tasks-kanban?board=${boardId}&card=${card.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      // Clipboard API unavailable/denied — the URL is already in the address bar.
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onChange({ title: title.trim() || card.title, description, assignee_name: assignee.trim(), due_date: dueDate || null });
      onClose();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="flex items-center gap-8">
            <h3 className="modal-title">{t('kanban.editCard')}</h3>
            <button
              type="button"
              className="btn-icon"
              title={linkCopied ? t('common.copied') : t('kanban.copyCardLink')}
              onClick={copyLink}
            >
              {linkCopied ? <Check size={14} /> : <LinkIcon size={14} />}
            </button>
          </div>
          <button className="close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">{t('common.title')}</label>
            <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus onFocus={titleLive.onFocus} onBlur={titleLive.onBlur} />
            <LiveDrafts drafts={titleLive.drafts} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('common.description')}</label>
            <BareTextarea value={description} onChange={setDescription} placeholder={t('kanban.optionalDetails')} liveKey={`card:${card.id}:description`} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('kanban.dueDate')}</label>
            <input className="form-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('kanban.assignee')}</label>
            {memberNames.length > 0 ? (
              <SearchableSelect
                value={assignee}
                options={memberNames}
                placeholder={t('kanban.unassigned')}
                onChange={(v) => setAssignee(String(v))}
              />
            ) : (
              <input className="form-input" value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder={t('kanban.unassigned')} />
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm text-err" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} /> {t('common.delete')}
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-accent" disabled={saving || !title.trim()} onClick={save}>{t('common.save')}</button>
        </div>
      </div>
      <ConfirmModal
        open={confirmDelete}
        title={t('kanban.deleteCardTitle')}
        message={t('kanban.deleteCardMsg')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={() => { setConfirmDelete(false); onDeleted(); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ── Members / invite modal ──

function MembersModal({
  members,
  ownerUserId,
  canManage,
  onInvite,
  onRemove,
  onClose,
}: {
  members: KanbanMember[];
  ownerUserId: number | null;
  canManage: boolean;
  onInvite: (userId: number) => Promise<void>;
  onRemove: (userId: number) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useT();
  const [users, setUsers] = useState<KanbanUser[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.kanbanUsers().then(setUsers).catch(() => {});
  }, []);

  const memberIds = useMemo(() => new Set(members.map(m => m.user_id)), [members]);
  const inviteOptions = useMemo(
    () => users
      .filter(u => !memberIds.has(u.id))
      .map(u => ({ value: u.id, label: u.name })),
    [users, memberIds],
  );

  const handleInvite = async (userId: number) => {
    setBusy(true);
    try { await onInvite(userId); } catch (err: any) { alert(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 className="modal-title">{t('kanban.members')}</h3>
          <button className="close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {canManage && (
            <div className="form-group">
              <label className="form-label">{t('kanban.invitePeople')}</label>
              <SearchableSelect
                value={''}
                options={inviteOptions}
                placeholder={inviteOptions.length ? t('kanban.pickPerson') : t('kanban.noOneToInvite')}
                onChange={(v) => { if (v !== '' && !busy) handleInvite(Number(v)); }}
              />
            </div>
          )}
          <div className="kb-members">
            {members.map(m => (
              <div key={m.user_id} className="kb-member">
                <div className="kb-member-main">
                  <span className="kb-member-name">
                    {m.name}
                    {ownerUserId !== null && m.user_id === ownerUserId && <span className="kb-member-owner"> · {t('kanban.owner')}</span>}
                  </span>
                  <span className="kb-member-email">{m.email}</span>
                </div>
                {canManage && ownerUserId !== null && m.user_id !== ownerUserId && (
                  <button className="kb-member-remove" title={t('kanban.removeMember')} onClick={() => onRemove(m.user_id)}>
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('common.done')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Board view ──

function BoardView({
  boardId,
  currentUserId,
  isAdmin,
  onBack,
}: {
  boardId: number;
  currentUserId: number;
  isAdmin: boolean;
  onBack: () => void;
}) {
  const { t } = useT();
  const [board, setBoard] = useState<KanbanBoardItem | null>(null);
  const [members, setMembers] = useState<KanbanMember[]>([]);
  const [cards, setCards] = useState<KanbanCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCard, setActiveCard] = useState<KanbanCard | null>(null);
  // Deep link into a specific card, e.g. #tasks-kanban?board=5&card=12 —
  // shared via the card modal's link button. Read once on mount; BoardView
  // remounts whenever the open board changes (see KanbanBoardsPage), so a
  // fresh read here always matches the board actually being shown.
  const [editingCardId, setEditingCardId] = useState<number | null>(() => {
    const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const c = p.get('card');
    return c ? Number(c) : null;
  });
  const [showMembers, setShowMembers] = useState(false);
  const [boardLinkCopied, setBoardLinkCopied] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // Keep the URL's card param in sync with the open card modal (via
  // replaceState, so it doesn't push a history entry) — the address bar is
  // then always a direct, shareable link back to this card.
  useEffect(() => {
    const current = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const next = editingCardId !== null ? String(editingCardId) : null;
    if (current.get('card') === next) return;
    if (next) current.set('card', next);
    else current.delete('card');
    window.history.replaceState(null, '', `#tasks-kanban?${current.toString()}`);
  }, [editingCardId]);

  const copyBoardLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}#tasks-kanban?board=${boardId}`;
    try {
      await navigator.clipboard.writeText(url);
      setBoardLinkCopied(true);
      setTimeout(() => setBoardLinkCopied(false), 1500);
    } catch {
      // Clipboard API unavailable/denied — the URL is already in the address bar.
    }
  };

  const loadBoard = useCallback(async () => {
    try {
      const data = await api.kanbanBoard(boardId);
      setBoard(data.board);
      setMembers(data.members);
      setCards(data.cards);
    } catch (err: any) {
      alert(err.message);
      onBack();
    } finally {
      setLoading(false);
    }
  }, [boardId, onBack]);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  // Live: who else is on this board (and which card they move or have open);
  // someone else's save refetches it — after this user's own drag, if any.
  const livePeople = usePresence(`kboard:${boardId}`, currentUserId);
  const liveFocusByCard = useMemo(() => focusMap(livePeople, 'card'), [livePeople]);
  const dragging = useRef(false);
  const reloadAfterDrag = useRef(false);
  useLiveChanges((c) => {
    if (c.area !== 'kanban' || (c.boardId && c.boardId !== boardId)) return;
    if (dragging.current) reloadAfterDrag.current = true;
    else loadBoard();
  });
  useEffect(() => {
    setLiveFocus(editingCardId ? { type: 'card', id: editingCardId, action: 'open' } : null);
  }, [editingCardId]);
  // movedId: the card was dropped in another column — the others see "moved by …" for a moment.
  const endDrag = (movedId?: number) => {
    dragging.current = false;
    const open: LiveFocus | null = editingCardId ? { type: 'card', id: editingCardId, action: 'open' } : null;
    if (movedId) flashMoved('card', movedId, open);
    else setLiveFocus(open);
    if (reloadAfterDrag.current) { reloadAfterDrag.current = false; setTimeout(loadBoard, 600); }
  };

  const canManage = !!board && (board.created_by === currentUserId || isAdmin);

  const cardsByColumn = useMemo(() => {
    const map: Record<KanbanColumnKey, KanbanCard[]> = {
      backlog: [], todo: [], in_progress: [], done: [], archived: [],
    };
    for (const c of cards) {
      if (isColumnKey(c.column_key)) map[c.column_key].push(c);
    }
    for (const k of Object.keys(map) as KanbanColumnKey[]) {
      map[k].sort((a, b) => a.sort_order - b.sort_order);
    }
    return map;
  }, [cards]);

  const handleDragStart = (e: DragStartEvent) => {
    const card = e.active.data.current?.card as KanbanCard | undefined;
    if (card) {
      setActiveCard(card);
      dragging.current = true;
      setLiveFocus({ type: 'card', id: card.id, action: 'drag' });
    }
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveCard(null);
    // Dropped in another column: the others see who moved it.
    const droppedCard = cards.find(c => `card-${c.id}` === String(e.active.id));
    const overKey = e.over ? String(e.over.id) : null;
    const overCol = overKey && (isColumnKey(overKey) ? overKey : cards.find(c => `card-${c.id}` === overKey)?.column_key);
    endDrag(droppedCard && overCol && overCol !== droppedCard.column_key ? droppedCard.id : undefined);
    const { active, over } = e;
    if (!over) return;

    const draggedId = Number(String(active.id).replace('card-', ''));
    const dragged = cards.find(c => c.id === draggedId);
    if (!dragged) return;

    const overId = String(over.id);
    let targetCol: KanbanColumnKey;
    let refCardId: number | null = null;

    if (isColumnKey(overId)) {
      targetCol = overId;
    } else if (overId.startsWith('card-')) {
      const overCardId = Number(overId.slice(5));
      if (overCardId === draggedId) return; // dropped on itself — no-op
      const overCard = cards.find(c => c.id === overCardId);
      if (!overCard) return;
      targetCol = overCard.column_key;
      refCardId = overCardId;
    } else {
      return;
    }

    // Build the new layout: remove dragged card, insert into the target column
    // before the reference card (or at the end), then renumber sequentially.
    const without = cards.filter(c => c.id !== draggedId);
    const targetCards = without.filter(c => c.column_key === targetCol);
    const insertIdx = refCardId !== null
      ? Math.max(0, targetCards.findIndex(c => c.id === refCardId))
      : targetCards.length;
    targetCards.splice(insertIdx, 0, { ...dragged, column_key: targetCol });
    const renumbered = targetCards.map((c, i) => ({ ...c, sort_order: i }));
    const others = without.filter(c => c.column_key !== targetCol);
    const newCards = [...others, ...renumbered];

    // No-op if nothing actually moved.
    const changed = newCards.filter(nc => {
      const oc = cards.find(c => c.id === nc.id);
      return !oc || oc.column_key !== nc.column_key || oc.sort_order !== nc.sort_order;
    });
    if (changed.length === 0) return;

    setCards(newCards);
    Promise.all(
      changed.map(c => api.kanbanUpdateCard(c.id, { column_key: c.column_key, sort_order: c.sort_order })),
    ).catch((err: Error & { code?: string }) => {
      loadBoard();
      if (err.code === 'TASK_STATUS_FORBIDDEN') alert(t('tasks.statusForbidden'));
    });
  };

  const addCard = async (columnKey: KanbanColumnKey, title: string, dueDate: string | null) => {
    try {
      const card = await api.kanbanAddCard(boardId, { title, column_key: columnKey, due_date: dueDate });
      setCards(prev => [...prev, card]);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const editingCard = editingCardId !== null ? cards.find(c => c.id === editingCardId) ?? null : null;

  const memberNames = useMemo(
    () => members.map(m => ({ value: m.name, label: m.name })),
    [members],
  );

  if (loading) {
    return <div className="page"><div className="spinner" /></div>;
  }
  if (!board) {
    return <div className="page"><p>{t('kanban.boardNotFound')}</p><button className="btn btn-ghost" onClick={onBack}>{t('common.back')}</button></div>;
  }

  return (
    <div className="kb-board-view">
      <LiveCursors people={livePeople} />
      <div className="kb-board-head">
        <button className="form-back" onClick={onBack}>
          <ArrowLeft className="icon-sm" /> {t('kanban.boards')}
        </button>
        <div className="kb-board-title">{board.name}</div>
        <PresenceAvatars people={livePeople} />
        <button
          type="button"
          className="btn-icon"
          title={boardLinkCopied ? t('common.copied') : t('kanban.copyBoardLink')}
          onClick={copyBoardLink}
        >
          {boardLinkCopied ? <Check size={14} /> : <LinkIcon size={14} />}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowMembers(true)}>
          <Users size={14} /> {members.length}
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => {
          const card = e.active.data.current?.card as KanbanCard | undefined;
          if (!card) return;
          const overId = e.over ? String(e.over.id) : null;
          const col = overId && (isColumnKey(overId) ? overId : cards.find(c => `card-${c.id}` === overId)?.column_key);
          setLiveFocus({ type: 'card', id: card.id, action: 'drag', ...(col ? { over: col } : {}) });
        }}
        onDragCancel={() => { setActiveCard(null); endDrag(); }}
      >
        <div className="kb-columns">
          {COLUMN_DEFS.map(col => (
            <Column
              key={col.key}
              columnKey={col.key}
              label={t(col.labelKey)}
              cards={cardsByColumn[col.key]}
              onOpenCard={setEditingCardId}
              onAddCard={(title, dueDate) => addCard(col.key, title, dueDate)}
              liveFocus={liveFocusByCard}
              ghosts={draggingOver(livePeople, 'card', col.key).flatMap(p => { const card = cards.find(c => c.id === p.focus!.id); return card && card.column_key !== col.key ? [{ person: p, card }] : []; })}
            />
          ))}
        </div>

        <DragOverlay>
          {activeCard && <CardOverlay card={activeCard} />}
        </DragOverlay>
      </DndContext>

      {editingCard && (
        <CardModal
          card={editingCard}
          boardId={boardId}
          memberNames={memberNames}
          onClose={() => setEditingCardId(null)}
          onChange={async (patch) => {
            await api.kanbanUpdateCard(editingCard.id, {
              title: patch.title,
              description: patch.description || null,
              assignee_name: patch.assignee_name || null,
              due_date: patch.due_date,
            });
            setCards(prev => prev.map(c => c.id === editingCard.id ? {
              ...c, title: patch.title, description: patch.description || null, assignee_name: patch.assignee_name || null, due_date: patch.due_date,
            } : c));
          }}
          onDeleted={async () => {
            try {
              await api.kanbanDeleteCard(editingCard.id);
              setCards(prev => prev.filter(c => c.id !== editingCard.id));
              setEditingCardId(null);
            } catch (err: any) {
              alert(err.message);
            }
          }}
        />
      )}

      {showMembers && (
        <MembersModal
          members={members}
          ownerUserId={board.created_by}
          canManage={canManage}
          onInvite={async (userId) => {
            const m = await api.kanbanAddMember(boardId, userId);
            setMembers(prev => [...prev, m].sort((a, b) => a.name.localeCompare(b.name)));
          }}
          onRemove={async (userId) => {
            await api.kanbanRemoveMember(boardId, userId);
            setMembers(prev => prev.filter(m => m.user_id !== userId));
          }}
          onClose={() => setShowMembers(false)}
        />
      )}
    </div>
  );
}

// ── Page ──

export function KanbanBoardsPage() {
  const { t } = useT();
  const { user } = useAuth();
  const { isAdmin, isDirection, loading: roleLoading } = useRole();
  const canSeeAll = isAdmin || isDirection;

  const [boards, setBoards] = useState<KanbanBoardItem[]>([]);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  // Admins and direction oversee every board, so default them to the full list
  // once their role resolves — otherwise a board created by someone else (e.g.
  // a teammate inviting only their own members) is hidden behind the "All
  // boards" toggle and looks missing. Applied once, so a manual switch to "My
  // boards" still sticks. Regular users stay scoped to their own boards.
  const [scopeReady, setScopeReady] = useState(false);
  // Deep link into a specific board, e.g. #tasks-kanban?board=5 — shared via
  // the board header's link button, so a board (and, via the card param a
  // teammate opens next, a card within it) has a fixed, shareable address
  // instead of always landing back on the board list.
  const [openBoardId, setOpenBoardId] = useState<number | null>(() => {
    const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const b = p.get('board');
    return b ? Number(b) : null;
  });
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  // Follow external hash changes (browser back/forward, or a link pasted
  // while already on this page) so the open board always matches the URL.
  useEffect(() => {
    const onHashChange = () => {
      const raw = window.location.hash;
      if (!raw.startsWith('#tasks-kanban')) return;
      const p = new URLSearchParams(raw.split('?')[1] || '');
      const b = p.get('board');
      setOpenBoardId(b ? Number(b) : null);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Keep the URL's board param in sync with the open board (via replaceState,
  // so switching boards doesn't pile up history entries). Dropping the card
  // param here too, since a stale card id shouldn't follow a board switch.
  useEffect(() => {
    const current = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const next = openBoardId !== null ? String(openBoardId) : null;
    if (current.get('board') === next) return;
    if (next) current.set('board', next);
    else current.delete('board');
    current.delete('card');
    const qs = current.toString();
    window.history.replaceState(null, '', `#tasks-kanban${qs ? `?${qs}` : ''}`);
  }, [openBoardId]);

  useEffect(() => {
    if (roleLoading || scopeReady) return;
    setScope(canSeeAll ? 'all' : 'mine');
    setScopeReady(true);
  }, [roleLoading, canSeeAll, scopeReady]);

  const loadBoards = useCallback(async () => {
    try {
      const list = await api.kanbanBoards(scope);
      setBoards(list);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  // Wait until the initial scope is settled from the role, so admins fetch the
  // full list directly instead of flashing "My boards" first.
  useEffect(() => { if (scopeReady) loadBoards(); }, [scopeReady, loadBoards]);

  const handleBack = useCallback(() => {
    setOpenBoardId(null);
    loadBoards();
  }, [loadBoards]);

  const createBoard = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const board = await api.kanbanCreateBoard(name);
      setNewName('');
      setBoards(prev => [board, ...prev]);
      setOpenBoardId(board.id);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCreating(false);
    }
  };

  if (!user) return null;

  if (openBoardId !== null) {
    return (
      <BoardView
        boardId={openBoardId}
        currentUserId={user.id}
        isAdmin={isAdmin}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="kb-page">
      <div className="toolbar page-toolbar">
        <div className="toolbar-filters">
          {canSeeAll && (
            <FilterSelect
              label={t('kanban.boardsLabel')}
              multiple={false}
              clearable={false}
              neutral="mine"
              selected={[scope]}
              options={[{ value: 'mine', label: t('kanban.myBoards') }, { value: 'all', label: t('kanban.allBoards') }]}
              onChange={(vals) => setScope((vals[0] ?? 'mine') as typeof scope)}
            />
          )}
        </div>
        <div className="toolbar-actions kb-create">
          <input
            className="form-input kb-create-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createBoard(); }}
            placeholder={t('kanban.newBoardPlaceholder')}
          />
          <button className="btn btn-accent btn-sm" onClick={createBoard} disabled={creating || !newName.trim()}>
            <Plus size={14} /> {t('common.create')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="page"><div className="spinner" /></div>
      ) : boards.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">
            {scope === 'all' ? t('kanban.noBoardsYet') : t('kanban.noBoardsShared')}
          </div>
          <div className="empty-state-desc">
            {t('kanban.emptyDesc')}
          </div>
        </div>
      ) : (
        <div className="kb-board-grid">
          {boards.map(b => (
            <button key={b.id} className="kb-board-card" onClick={() => setOpenBoardId(b.id)}>
              <div className="kb-board-card-name">{b.name}</div>
              <div className="kb-board-card-meta">
                <Users size={13} /> {(b.member_count ?? 0) === 1
                  ? t('kanban.memberCountOne', { n: b.member_count ?? 0 })
                  : t('kanban.memberCountMany', { n: b.member_count ?? 0 })}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
